import * as XLSX from 'xlsx';
import type { ParseResult } from './parseResult';

// Columnas esperadas (fila 1): DNI, Nombres, Apellidos, Celular, Correo, Banco, Cuenta, CCI
export interface WorkerImportRow {
  dni: string;
  firstNames: string;
  lastNames: string;
  phone: string;
  email: string;
  bank: string;
  accountNumber: string;
  cci: string;
  rowNumber: number;
}

const ALL_HEADERS = ['DNI', 'Nombres', 'Apellidos', 'Celular', 'Correo', 'Banco', 'Cuenta', 'CCI'] as const;
/**
 * Solo el documento y el nombre son obligatorios.
 *
 * "Celular" y "Correo" salieron de esta lista el 2026-08-12, con el primer lote de gente de
 * fines de semana: la nómina que manda operaciones trae documento, nombres, apellidos y fecha de
 * nacimiento, y **no trae ni celular ni correo**. Exigirlos no mejoraba el dato — solo impedía
 * cargar a once personas que sí existen y que necesitan su acceso para poder ser asignadas.
 * El correo de ACCESO no hace falta: lo arma el servidor desde el documento
 * (`approve-worker`, `<documento>@campo.treid.pe`).
 */
const REQUIRED_HEADERS = ['DNI', 'Nombres', 'Apellidos'] as const;
/**
 * 7 a 9 dígitos, no 8 exactos.
 *
 * El **carnet de extranjería tiene 7 dígitos legítimos** y en el piloto hay dos personas así
 * (María Laura Navarro y Miguel Ángel Díaz). Con `\d{8}` el alta masiva no podía cargar a ningún
 * extranjero, y el error decía "DNI inválido" sobre un documento perfectamente válido.
 */
const DNI_RE = /^\d{7,9}$/;
const PHONE_RE = /^9\d{8}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function cellText(v: unknown): string {
  if (v === null || v === undefined) return '';
  return String(v).trim();
}

/** Excel suele convertir DNI a número y perder ceros a la izquierda: los restauramos. */
function cellDni(v: unknown): string {
  if (typeof v === 'number' && Number.isInteger(v) && v >= 0 && v < 100000000) {
    return String(v).padStart(8, '0');
  }
  return cellText(v);
}

export function parseWorkersFile(data: ArrayBuffer): ParseResult<WorkerImportRow> {
  const rows: WorkerImportRow[] = [];
  const errors: { rowNumber: number; message: string }[] = [];

  const wb = XLSX.read(data, { type: 'array' });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) return { rows, errors: [{ rowNumber: 0, message: 'El archivo no tiene hojas' }] };

  const aoa = XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[sheetName], {
    header: 1, defval: '', blankrows: true,
  });
  if (aoa.length === 0) return { rows, errors: [{ rowNumber: 0, message: 'La hoja está vacía' }] };

  const headerRow = (aoa[0] ?? []).map(cellText);
  const col: Record<string, number> = {};
  for (const h of ALL_HEADERS) {
    col[h] = headerRow.findIndex((t) => t.toLowerCase() === h.toLowerCase());
  }
  const missing = REQUIRED_HEADERS.filter((h) => col[h] === -1);
  if (missing.length > 0) {
    return { rows, errors: [{ rowNumber: 1, message: `Faltan columnas obligatorias: ${missing.join(', ')}` }] };
  }
  const get = (r: unknown[], h: string): unknown => (col[h] === -1 ? '' : r[col[h]]);

  const seenDni = new Map<string, number>(); // dni → primera fila donde aparece

  for (let i = 1; i < aoa.length; i++) {
    const rowNumber = i + 1;
    const r = aoa[i] ?? [];
    if (r.every((c) => cellText(c) === '')) continue;

    const rowErrors: string[] = [];
    const dni = cellDni(get(r, 'DNI'));
    const firstNames = cellText(get(r, 'Nombres'));
    const lastNames = cellText(get(r, 'Apellidos'));
    const phone = cellText(get(r, 'Celular'));
    const email = cellText(get(r, 'Correo'));

    if (!DNI_RE.test(dni)) {
      rowErrors.push(`Documento inválido (7 a 9 dígitos; el C.E. tiene 7): "${dni}"`);
    }
    if (firstNames === '') rowErrors.push('Nombres es obligatorio');
    if (lastNames === '') rowErrors.push('Apellidos es obligatorio');
    // Celular y correo son OPCIONALES, pero si vienen tienen que estar bien: un celular mal
    // escrito es peor que ninguno, porque el coordinador cree que puede llamar.
    if (phone !== '' && !PHONE_RE.test(phone)) {
      rowErrors.push(`Celular inválido (9 dígitos iniciando en 9): "${phone}"`);
    }
    if (email !== '' && !EMAIL_RE.test(email)) rowErrors.push(`Correo inválido: "${email}"`);

    if (DNI_RE.test(dni)) {
      const firstRow = seenDni.get(dni);
      if (firstRow !== undefined) {
        rowErrors.push(`DNI duplicado en el archivo (ya aparece en la fila ${firstRow})`);
      } else {
        seenDni.set(dni, rowNumber);
      }
    }

    if (rowErrors.length > 0) {
      errors.push({ rowNumber, message: rowErrors.join('; ') });
      continue;
    }
    rows.push({
      dni, firstNames, lastNames, phone, email,
      bank: cellText(get(r, 'Banco')),
      accountNumber: cellText(get(r, 'Cuenta')),
      cci: cellText(get(r, 'CCI')),
      rowNumber,
    });
  }
  return { rows, errors };
}
